import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Check,
  FileText,
  ImagePlus,
  LoaderCircle,
  MapPin,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Users,
} from 'lucide-react';
import {
  hqMonthlyMeetingDraftSchema,
  type HqHistoricalReportBody,
  type HqHistoricalSummary,
  type HqMonthlyMeetingAttachment,
  type HqMonthlyMeetingDocument,
  type HqMonthlyMeetingDraft,
  type HqMonthlyMeetingsLoadResult,
} from '@kop/contracts';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import { loadSavedHeadquartersReport } from '../briefing/headquarters-saved-read';
import { MonthlyMeetingReview } from '../briefing/monthly-meeting-review';
import {
  MeetingAttachmentList,
  MeetingAttachmentViewer,
  MeetingPhoto,
  MeetingPhotoSpotlight,
} from './attachments';
import { MeetingAttendeesEditor, MeetingBasicEditor, MeetingMinutesEditor } from './editor';
import {
  editSavedMeeting,
  meetingStatusLabel,
  meetingValidationMessage,
  newMeetingEditor,
  type MeetingEditor,
} from './model';
import {
  createFirebaseHqMonthlyMeetingsRepository,
  type HqMonthlyMeetingsRepository,
  type MeetingUploadProgress,
} from './repository';
import './workspace.css';

type HistoricalMeeting = Extract<HqHistoricalReportBody, { kind: 'monthly-meeting' }>;
type Selection = { kind: 'native' | 'historical'; id: string } | null;
type Tab = 'prepare' | 'minutes' | 'materials';
type Props = {
  uid: string;
  initialYear: number;
  historical: HqHistoricalSummary | null;
  initialHistoricalId?: string | null;
  featureIds: readonly string[];
  onOpen: (id: string, readSiteId?: string) => void;
  onSaved?: () => void;
  repository?: HqMonthlyMeetingsRepository | null;
};
const dateLabel = (value: string | null) => (value ? value.replaceAll('-', '.') : '일자 미정');
const savedLabel = (value: string) =>
  new Date(value).toLocaleString('ko-KR', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
const tabItems: readonly [Tab, string][] = [
  ['prepare', '사업장 회의자료'],
  ['minutes', '회의록'],
  ['materials', '참석자 · 자료'],
];

export function HqMonthlyMeetingsWorkspace(props: Props) {
  return <MonthlyMeetingsSession key={props.uid} {...props} />;
}

function MonthlyMeetingsSession({
  uid,
  initialYear,
  historical,
  initialHistoricalId,
  featureIds,
  onOpen,
  onSaved,
  repository: provided,
}: Props) {
  const repository = useMemo(
    () => (provided === undefined ? createFirebaseHqMonthlyMeetingsRepository(uid) : provided),
    [provided, uid],
  );
  const [year, setYear] = useState(initialYear),
    [month, setMonth] = useState(0),
    [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Selection>(
    initialHistoricalId ? { kind: 'historical', id: initialHistoricalId } : null,
  );
  const [page, setPage] = useState<HqMonthlyMeetingsLoadResult | null>(null),
    [document, setDocument] = useState<HqMonthlyMeetingDocument | null>(null);
  const [original, setOriginal] = useState<HistoricalMeeting | null>(null),
    [editor, setEditor] = useState<MeetingEditor | null>(null);
  const [busy, setBusy] = useState(true),
    [saving, setSaving] = useState(false),
    [locked, setLocked] = useState(false);
  const [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [revision, setRevision] = useState(0);
  const [uploading, setUploading] = useState(false),
    [listPage, setListPage] = useState(0);
  const [tab, setTab] = useState<Tab>('minutes'),
    [siteMaterials, setSiteMaterials] = useState(false);
  const [uploadKind, setUploadKind] = useState<HqMonthlyMeetingAttachment['kind']>('photo');
  const [upload, setUpload] = useState<
    (MeetingUploadProgress & { name: string; index: number; total: number }) | null
  >(null);
  const [viewer, setViewer] = useState<{
    meetingId: string;
    attachment: HqMonthlyMeetingAttachment;
  } | null>(null);
  const generation = useRef(0),
    controller = useRef<AbortController | null>(null);
  const pendingSave = useRef<{ payload: string; commandId: string } | null>(null);
  const handledRevision = useRef(revision);
  const closeViewer = useCallback(() => setViewer(null), []);
  const invalidateRequests = useCallback(() => {
    generation.current++;
  }, []);
  const cancelWork = useCallback(() => {
    generation.current++;
    controller.current?.abort();
  }, []);
  const selectionId = selection?.id ?? '',
    selectionKind = selection?.kind ?? '';
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
      cancelWork();
      setLocked(true);
      setPage(null);
      setDocument(null);
      setOriginal(null);
      setEditor(null);
      setViewer(null);
      setSelection(null);
      setError('');
      setMessage('');
      setUpload(null);
      setUploading(false);
      setSaving(false);
      pendingSave.current = null;
    });
    return () => {
      cancelWork();
      unsubscribe();
    };
  }, [cancelWork]);
  useEffect(() => {
    const run = ++generation.current;
    const input = selectionKind === 'native' ? { year, meetingId: selectionId } : { year };
    const saved = repository?.peek?.(input) ?? null;
    setPage(saved);
    setDocument(saved?.document ?? null);
    setOriginal(null);
    setBusy(!saved);
    setError('');
    if (locked || !repository) {
      setBusy(false);
      return;
    }
    const fresh = revision !== handledRevision.current;
    handledRevision.current = revision;
    void Promise.all([
      repository.load(input, fresh),
      selectionKind === 'historical'
        ? loadSavedHeadquartersReport(uid, selectionId)
        : Promise.resolve(null),
    ])
      .then(([result, source]) => {
        if (generation.current !== run) return;
        if (
          source &&
          (source.kind !== 'monthly-meeting' || source.year !== year || source.id !== selectionId)
        )
          throw Error('원본 회의가 일치하지 않습니다.');
        setPage(result);
        setDocument(result.document);
        setOriginal(source?.kind === 'monthly-meeting' ? source : null);
      })
      .catch(() => {
        if (generation.current !== run) return;
        const retained = repository.peek?.(input) ?? null;
        setPage(retained);
        setDocument(retained?.document ?? null);
        setError(
          retained
            ? '최근 확인한 회의를 표시하고 있습니다. 최신 내용은 새로고침으로 확인해 주세요.'
            : '회의 자료를 불러오지 못했습니다. 다시 시도해 주세요.',
        );
      })
      .finally(() => {
        if (generation.current === run) setBusy(false);
      });
    return invalidateRequests;
  }, [uid, year, selectionKind, selectionId, revision, repository, locked, invalidateRequests]);

  const canEdit = page?.canEdit === true && !locked && repository !== null;
  const working = saving || uploading;
  const visibleHistorical = historical?.year === year ? historical.monthlyMeetings : [];
  const matches = (title: string, candidateMonth: number) =>
    (!month || month === candidateMonth) &&
    title.toLocaleLowerCase('ko-KR').includes(search.trim().toLocaleLowerCase('ko-KR'));
  const nativeRows = (page?.records ?? []).filter((row) => matches(row.title, row.month));
  const pageCount = Math.max(1, Math.ceil(nativeRows.length / 6));
  const currentPage = Math.min(listPage, pageCount - 1);
  const linkedHistoricalIds = new Set(
    (page?.records ?? []).map((row) => row.historicalReferenceId).filter(Boolean),
  );
  const historyRows = visibleHistorical.filter(
    (row) => !linkedHistoricalIds.has(row.id) && matches(row.title, Number(row.month.slice(5, 7))),
  );
  const activeDraft = editor?.draft ?? document?.draft ?? null;
  const activeId = editor?.meetingId ?? document?.meetingId;
  const detailed = editor !== null || selection !== null;
  const updateDraft = (draft: HqMonthlyMeetingDraft) => {
    pendingSave.current = null;
    setMessage('');
    setEditor((current) => (current ? { ...current, draft } : null));
  };
  const leaveEditor = () => !editor || window.confirm('저장하지 않은 입력을 닫을까요?');
  const goList = () => {
    if (working || !leaveEditor()) return;
    setSelection(null);
    setEditor(null);
    setDocument(null);
    setOriginal(null);
    setViewer(null);
    setError('');
    setMessage('');
    setUpload(null);
    setSiteMaterials(false);
  };
  const select = (kind: 'native' | 'historical', id: string) => {
    if (working || !leaveEditor()) return;
    setEditor(null);
    setDocument(null);
    setOriginal(null);
    setSelection({ kind, id });
    setTab('minutes');
    setSiteMaterials(false);
    setMessage('');
    setUpload(null);
  };
  const create = () => {
    if (!canEdit || working || !leaveEditor()) return;
    setSelection(null);
    setDocument(null);
    setOriginal(null);
    setEditor(
      newMeetingEditor(
        year,
        month ||
          Number(new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(5, 7)),
      ),
    );
    setTab('minutes');
    setError('');
    setMessage('');
    setUpload(null);
    setSiteMaterials(false);
  };
  const supplement = () => {
    if (!canEdit || !original) return;
    setEditor(
      newMeetingEditor(year, Number(original.data.month.slice(5, 7)), {
        id: original.id,
        title: original.title,
        round: original.data.round || null,
        meetingDate: original.data.meetingDate,
      }),
    );
    setSelection(null);
    setOriginal(null);
    setTab('minutes');
    setMessage('');
    setError('');
  };
  const save = async () => {
    if (!canEdit || !editor || working || !repository) return;
    const parsed = hqMonthlyMeetingDraftSchema.safeParse(editor.draft);
    if (!parsed.success) {
      setError(
        meetingValidationMessage(
          parsed.error.issues[0]?.path ?? [],
          editor.draft.status === 'completed',
        ),
      );
      return;
    }
    const payload = JSON.stringify({
      id: editor.meetingId,
      version: editor.version,
      draft: parsed.data,
    });
    const commandId =
      pendingSave.current?.payload === payload
        ? pendingSave.current.commandId
        : `hq-meeting-save-${crypto.randomUUID()}`;
    pendingSave.current = { payload, commandId };
    const run = generation.current;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await repository.save({
        commandId,
        meetingId: editor.meetingId,
        expectedVersion: editor.version,
        draft: parsed.data,
      });
      if (generation.current !== run) return;
      pendingSave.current = null;
      setEditor(null);
      setDocument(result.document);
      setSelection({ kind: 'native', id: result.document.meetingId });
      setRevision((value) => value + 1);
      setMessage('회의 내용을 저장했습니다.');
      onSaved?.();
    } catch {
      if (generation.current === run)
        setError(
          '저장하지 못했습니다. 입력한 내용은 유지됩니다. 변경된 자료나 연결 상태를 확인한 뒤 다시 저장해 주세요.',
        );
    } finally {
      if (generation.current === run) setSaving(false);
    }
  };
  const uploadFiles = async (files: File[]) => {
    if (!canEdit || !editor || !repository || working || !files.length) return;
    if (editor.draft.attachments.length + files.length > 30) {
      setError('첨부 자료는 최대 30개까지 등록할 수 있습니다.');
      return;
    }
    const run = generation.current,
      id = editor.meetingId,
      abort = new AbortController();
    controller.current = abort;
    setError('');
    setMessage('');
    setUploading(true);
    try {
      for (const [index, file] of files.entries()) {
        const attachment = await repository.upload(id, file, uploadKind, abort.signal, (status) => {
          if (generation.current === run)
            setUpload({ ...status, name: file.name, index: index + 1, total: files.length });
        });
        if (generation.current !== run) return;
        pendingSave.current = null;
        setEditor((current) =>
          current?.meetingId === id
            ? {
                ...current,
                draft: {
                  ...current.draft,
                  attachments: [...current.draft.attachments, attachment],
                },
              }
            : current,
        );
      }
    } catch (reason) {
      if (generation.current === run) {
        setUpload(null);
        setError(
          reason instanceof Error && reason.message.includes('등록해')
            ? reason.message
            : '자료를 첨부하지 못했습니다. 파일을 확인하고 다시 선택해 주세요.',
        );
      }
    } finally {
      if (controller.current === abort) controller.current = null;
      if (generation.current === run) setUploading(false);
    }
  };
  const openAttachment = (attachment: HqMonthlyMeetingAttachment) => {
    if (activeId) setViewer({ meetingId: activeId, attachment });
  };
  const openOriginal = (id: string) => select('historical', id);
  const historicalName = activeDraft?.historicalReferenceId
    ? visibleHistorical.find((row) => row.id === activeDraft.historicalReferenceId)?.title
    : null;
  const monthKey = `${year}-${String(activeDraft?.month ?? (month || Number(new Date().getMonth()) + 1)).padStart(2, '0')}`;
  const siteReview = (
    <div className="hqmm-site-material">
      <MonthlyMeetingReview
        key={`${uid}:${monthKey}`}
        uid={uid}
        siteId={null}
        initialMonth={monthKey}
        onClose={() => {
          setSiteMaterials(false);
          setTab('minutes');
        }}
        onOpen={onOpen}
        featureIds={featureIds}
        presentation="page"
      />
    </div>
  );

  return (
    <section
      className={`hqmm ${!detailed && !siteMaterials ? 'hqmm--list' : ''}`}
      aria-label="본사 안전보건 월회의"
      aria-busy={busy || saving}
    >
      <div className="hqmm-toolbar">
        <div>
          {detailed ? (
            <button type="button" className="hqmm-outline" onClick={goList} disabled={working}>
              <ArrowLeft size={15} />
              회의 목록
            </button>
          ) : (
            <>
              <label className="hqmm-filter">
                <span>연도</span>
                <select
                  aria-label="월회의 연도"
                  value={year}
                  disabled={working}
                  onChange={(event) => {
                    setYear(Number(event.target.value));
                    setSelection(null);
                    setEditor(null);
                    setListPage(0);
                  }}
                >
                  {Array.from(
                    new Set([
                      year,
                      ...Array.from(
                        { length: 7 },
                        (_, index) => new Date().getFullYear() + 1 - index,
                      ),
                    ]),
                  )
                    .sort((a, b) => b - a)
                    .map((value) => (
                      <option key={value} value={value}>
                        {value}년
                      </option>
                    ))}
                </select>
              </label>
              <label className="hqmm-filter">
                <span>대상 월</span>
                <select
                  aria-label="월회의 대상 월"
                  value={month}
                  onChange={(event) => {
                    setMonth(Number(event.target.value));
                    setListPage(0);
                  }}
                >
                  <option value="0">전체</option>
                  {Array.from({ length: 12 }, (_, index) => (
                    <option key={index} value={index + 1}>
                      {index + 1}월
                    </option>
                  ))}
                </select>
              </label>
              <input
                className="hqmm-search"
                aria-label="회의명 검색"
                placeholder="회의명 검색"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setListPage(0);
                }}
              />
            </>
          )}
        </div>
        <div>
          {editor ? (
            <>
              <span className="hqmm-unsaved">저장 전</span>
              <button
                type="button"
                className="hqmm-primary"
                onClick={() => void save()}
                disabled={!canEdit || working || busy}
              >
                {saving ? <LoaderCircle className="hqmm-spin" size={16} /> : <Save size={16} />}저장
              </button>
            </>
          ) : document && canEdit ? (
            <button
              type="button"
              className="hqmm-primary"
              onClick={() => setEditor(editSavedMeeting(document))}
              disabled={busy}
            >
              <Pencil size={15} />
              수정
            </button>
          ) : original && canEdit ? (
            <button type="button" className="hqmm-primary" onClick={supplement}>
              <Plus size={16} />
              보완 기록
            </button>
          ) : (
            !detailed && (
              <button
                type="button"
                className="hqmm-primary"
                disabled={!canEdit || busy}
                onClick={create}
              >
                <Plus size={17} />
                월회의 작성
              </button>
            )
          )}
          {!editor && (
            <button
              type="button"
              className="hqmm-icon-button hqmm-reload"
              aria-label="회의 목록 새로고침"
              onClick={() => setRevision((value) => value + 1)}
              disabled={busy || locked}
            >
              <RefreshCw size={17} />
            </button>
          )}
        </div>
      </div>
      {locked ? (
        <p role="alert" className="hqmm-error">
          접속 정보가 변경되었습니다. 다시 접속해 주세요.
        </p>
      ) : !repository ? (
        <p role="alert" className="hqmm-error">
          본사 회의 기능에 연결할 수 없습니다. 로그인 상태와 시스템 설정을 확인해 주세요.
        </p>
      ) : null}
      {error && (
        <p role="alert" className="hqmm-error">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="hqmm-success">
          <Check size={16} />
          {message}
        </p>
      )}
      {busy && (
        <div className="hqmm-loading" role="status">
          <LoaderCircle size={22} className="hqmm-spin" />
          회의 자료를 불러오는 중입니다.
        </div>
      )}
      {!busy && repository && !locked && !detailed && (
        <>
          <nav className="hqmm-tabs" aria-label="월회의 자료 구분">
            <button
              type="button"
              aria-pressed={!siteMaterials}
              onClick={() => setSiteMaterials(false)}
            >
              본사 월회의
            </button>
            <button
              type="button"
              aria-pressed={siteMaterials}
              onClick={() => setSiteMaterials(true)}
            >
              사업장 회의자료
            </button>
          </nav>
          {siteMaterials ? (
            siteReview
          ) : (
            <div className="hqmm-list">
              <div className="hqmm-record-grid">
                {nativeRows.slice(currentPage * 6, (currentPage + 1) * 6).map((row) => (
                  <button
                    type="button"
                    className={`hqmm-record ${row.coverAttachment ? 'hqmm-record--photo' : ''}`}
                    key={row.meetingId}
                    onClick={() => select('native', row.meetingId)}
                  >
                    {row.coverAttachment ? (
                      <div className="hqmm-cover">
                        <MeetingPhoto
                          meetingId={row.meetingId}
                          attachment={row.coverAttachment}
                          repository={repository}
                        />
                      </div>
                    ) : (
                      <div className="hqmm-record-icon">
                        <Users size={25} />
                        <span>{row.month}월</span>
                      </div>
                    )}
                    <div className="hqmm-record-content">
                      <span className={`hqmm-status hqmm-status--${row.status}`}>
                        {meetingStatusLabel(row.status)}
                      </span>
                      <h2>{row.title}</h2>
                      <p>
                        <CalendarDays size={13} />
                        {dateLabel(row.meetingDate)}
                      </p>
                      <footer>
                        <span>
                          <Users size={13} />
                          {row.attendeeCount}명
                        </span>
                        <span>
                          <Paperclip size={13} />
                          {row.attachmentCount}개
                        </span>
                        <ArrowUpRight size={17} />
                      </footer>
                    </div>
                  </button>
                ))}
              </div>
              {nativeRows.length > 0 && (
                <nav className="hqmm-pagination" aria-label="월회의 목록 페이지">
                  <span>전체 {nativeRows.length}건</span>
                  <div>
                    <button
                      type="button"
                      className="hqmm-icon-button"
                      aria-label="이전 회의 목록"
                      disabled={currentPage === 0}
                      onClick={() => setListPage(currentPage - 1)}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span>
                      {currentPage + 1} / {pageCount}
                    </span>
                    <button
                      type="button"
                      className="hqmm-icon-button"
                      aria-label="다음 회의 목록"
                      disabled={currentPage + 1 === pageCount}
                      onClick={() => setListPage(currentPage + 1)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </nav>
              )}
              {historyRows.length > 0 && (
                <section className="hqmm-panel hqmm-originals">
                  <header>
                    <h2>
                      <FileText size={17} />
                      기존 월회의 원본
                    </h2>
                    <span>원본 보존</span>
                  </header>
                  <div>
                    {historyRows.map((row) => (
                      <button
                        type="button"
                        key={row.id}
                        onClick={() => select('historical', row.id)}
                      >
                        <span className="hqmm-original-round">{row.round}차</span>
                        <span>
                          <strong>{row.title}</strong>
                          <small>{dateLabel(row.meetingDate)}</small>
                        </span>
                        <span className="hqmm-original-tag">원본</span>
                        <ArrowUpRight size={17} />
                      </button>
                    ))}
                  </div>
                </section>
              )}
              {!nativeRows.length && !historyRows.length && !error && (
                <div className="hqmm-empty">
                  <CalendarDays size={34} />
                  <strong>등록한 월회의가 없습니다.</strong>
                  {canEdit && (
                    <button type="button" className="hqmm-outline" onClick={create}>
                      <Plus size={15} />
                      월회의 작성
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {!busy && repository && !locked && detailed && activeDraft && activeId && (
        <>
          <MeetingPhotoSpotlight
            meetingId={activeId}
            title={activeDraft.title}
            attachments={activeDraft.attachments}
            repository={repository}
            onOpen={openAttachment}
          />
          {editor ? (
            <MeetingBasicEditor
              draft={editor.draft}
              sites={page?.sites ?? []}
              disabled={working || !canEdit}
              onChange={updateDraft}
            />
          ) : (
            <section className="hqmm-meeting-heading">
              <div>
                <span className={`hqmm-status hqmm-status--${activeDraft.status}`}>
                  {meetingStatusLabel(activeDraft.status)}
                </span>
                <h2>{activeDraft.title}</h2>
                <p>
                  <CalendarDays size={15} />
                  {dateLabel(activeDraft.meetingDate)}
                  {activeDraft.startsAt && ` ${activeDraft.startsAt}`}
                  <MapPin size={15} />
                  {activeDraft.location || '장소 미기재'}
                  <Users size={15} />
                  {activeDraft.facilitator || '담당 미기재'}
                </p>
              </div>
              <small>{document ? `${savedLabel(document.savedAt)} 저장` : ''}</small>
            </section>
          )}
          {activeDraft.historicalReferenceId && (
            <div className="hqmm-reference">
              <FileText size={15} />
              <span>원본 보완 · {historicalName ?? '기존 월회의'}</span>
              <button
                type="button"
                disabled={working}
                onClick={() => openOriginal(activeDraft.historicalReferenceId!)}
              >
                원본 보기
                <ArrowUpRight size={13} />
              </button>
            </div>
          )}
          <nav className="hqmm-tabs" aria-label="회의 상세 항목">
            {tabItems.map(([id, label]) => (
              <button type="button" key={id} aria-pressed={tab === id} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </nav>
          {tab === 'prepare' ? (
            siteReview
          ) : tab === 'minutes' ? (
            editor ? (
              <MeetingMinutesEditor
                draft={editor.draft}
                sites={page?.sites ?? []}
                disabled={working || !canEdit}
                onChange={updateDraft}
              />
            ) : (
              <MeetingMinutesRead draft={activeDraft} sites={page?.sites ?? []} />
            )
          ) : (
            <>
              {editor ? (
                <MeetingAttendeesEditor
                  draft={editor.draft}
                  sites={page?.sites ?? []}
                  disabled={working || !canEdit}
                  onChange={updateDraft}
                />
              ) : (
                <MeetingAttendeesRead draft={activeDraft} />
              )}
              <section className="hqmm-panel">
                <header>
                  <h2>
                    <Paperclip size={17} />
                    사진·참석 서명지·회의 자료
                  </h2>
                  {editor && (
                    <div className="hqmm-upload-tools">
                      <select
                        aria-label="첨부 자료 구분"
                        value={uploadKind}
                        onChange={(event) =>
                          setUploadKind(event.target.value as HqMonthlyMeetingAttachment['kind'])
                        }
                        disabled={working || !canEdit}
                      >
                        <option value="photo">회의 사진</option>
                        <option value="signature">참석 서명지 PDF</option>
                        <option value="document">회의 자료 PDF</option>
                      </select>
                      <label
                        className={`hqmm-outline hqmm-file-input ${working || !canEdit ? 'is-disabled' : ''}`}
                      >
                        <ImagePlus size={16} />
                        자료 추가
                        <input
                          type="file"
                          multiple
                          disabled={working || !canEdit}
                          accept={
                            uploadKind === 'photo'
                              ? 'image/jpeg,image/png,image/webp'
                              : 'application/pdf'
                          }
                          onChange={(event) => {
                            const files = [...(event.target.files ?? [])];
                            event.target.value = '';
                            void uploadFiles(files);
                          }}
                        />
                      </label>
                    </div>
                  )}
                </header>
                {upload && (
                  <div className="hqmm-upload-progress" role="status">
                    <div>
                      <span>
                        {upload.index}/{upload.total} · {upload.name}
                      </span>
                      <strong>
                        {upload.phase === 'done'
                          ? '첨부 완료'
                          : upload.phase === 'preparing'
                            ? '파일 확인 중'
                            : upload.phase === 'committing'
                              ? '등록 확인 중'
                              : `${upload.progress}%`}
                      </strong>
                    </div>
                    <progress max="100" value={upload.progress} />
                  </div>
                )}
                <MeetingAttachmentList
                  attachments={activeDraft.attachments}
                  onOpen={openAttachment}
                  onRemove={
                    editor && canEdit && !working
                      ? (id) =>
                          updateDraft({
                            ...editor.draft,
                            attachments: editor.draft.attachments.filter(
                              (row) => row.manifestId !== id,
                            ),
                          })
                      : undefined
                  }
                />
              </section>
            </>
          )}
          {editor && (
            <footer className="hqmm-savebar">
              <label>
                <input
                  type="checkbox"
                  checked={editor.draft.status === 'completed'}
                  disabled={working || !canEdit}
                  onChange={(event) =>
                    updateDraft({
                      ...editor.draft,
                      status: event.target.checked ? 'completed' : 'draft',
                    })
                  }
                />
                회의 완료
              </label>
              <div>
                <button
                  type="button"
                  className="hqmm-outline"
                  disabled={working}
                  onClick={() => {
                    if (leaveEditor()) {
                      setEditor(null);
                      setUpload(null);
                    }
                  }}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="hqmm-primary"
                  disabled={!canEdit || working}
                  onClick={() => void save()}
                >
                  <Save size={16} />
                  저장
                </button>
              </div>
            </footer>
          )}
        </>
      )}
      {!busy && original && !editor && <HistoricalMeetingRead report={original} />}
      {viewer && repository && !locked && (
        <MeetingAttachmentViewer
          key={`${viewer.meetingId}:${viewer.attachment.manifestId}`}
          {...viewer}
          repository={repository}
          onClose={closeViewer}
        />
      )}
    </section>
  );
}

function MeetingMinutesRead({
  draft,
  sites,
}: {
  draft: HqMonthlyMeetingDraft;
  sites: readonly { id: string; name: string }[];
}) {
  return (
    <>
      {draft.agenda.length > 0 && (
        <section className="hqmm-panel">
          <header>
            <h2>
              <FileText size={17} />
              회의 안건·결정사항
            </h2>
          </header>
          <div className="hqmm-agenda-list">
            {draft.agenda.map((row, index) => (
              <article className="hqmm-agenda hqmm-agenda--read" key={row.id}>
                <span className="hqmm-agenda-number">{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{row.title}</h3>
                  <div className="hqmm-agenda-read-columns">
                    <section>
                      <h4>논의 내용</h4>
                      <p>{row.notes || '미기재'}</p>
                    </section>
                    <section>
                      <h4>결정사항·후속 조치</h4>
                      <p>{row.decision || '미기재'}</p>
                    </section>
                  </div>
                  <footer>
                    <span>{sites.find((site) => site.id === row.siteId)?.name ?? '본사·공통'}</span>
                    {row.owner && <span>담당 {row.owner}</span>}
                    {row.dueDate && <span>기한 {dateLabel(row.dueDate)}</span>}
                  </footer>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="hqmm-panel">
        <header>
          <h2>
            <FileText size={17} />
            회의록
          </h2>
        </header>
        <div className="hqmm-minutes">{draft.minutes || '작성한 회의록이 없습니다.'}</div>
      </section>
    </>
  );
}
function MeetingAttendeesRead({ draft }: { draft: HqMonthlyMeetingDraft }) {
  return (
    <section className="hqmm-panel">
      <header>
        <h2>
          <Users size={17} />
          참석자 <span>{draft.attendees.length}명</span>
        </h2>
      </header>
      <div className="hqmm-people">
        {draft.attendees.length ? (
          draft.attendees.map((person) => (
            <article key={person.id}>
              <span>
                <Users size={18} />
              </span>
              <div>
                <strong>{person.name}</strong>
                <small>{[person.organization, person.position].filter(Boolean).join(' · ')}</small>
              </div>
            </article>
          ))
        ) : (
          <p className="hqmm-empty-small">등록한 참석자가 없습니다.</p>
        )}
      </div>
    </section>
  );
}
function HistoricalMeetingRead({ report }: { report: HistoricalMeeting }) {
  return (
    <article className="hqmm-historical">
      <section className="hqmm-meeting-heading">
        <div>
          <span className="hqmm-original-tag">기존 원본</span>
          <h2>{report.title}</h2>
          <p>
            <CalendarDays size={15} />
            {report.data.meetingDate
              ? dateLabel(report.data.meetingDate)
              : `일자 확인 필요 (${report.data.dateCandidates.join(' / ')})`}
            <MapPin size={15} />
            {report.data.place ?? '장소 미기재'}
          </p>
        </div>
      </section>
      <div className="hqmm-reference">
        <FileText size={16} />
        원본은 그대로 보존합니다. 추가할 회의 내용과 자료는 보완 기록에 남길 수 있습니다.
      </div>
      <section className="hqmm-panel">
        <header>
          <h2>
            <FileText size={17} />
            기존 회의록
          </h2>
        </header>
        <div className="hqmm-minutes">
          {report.data.minutes.map((row, index) => (
            <p key={`${row.address}:${index}`}>{row.text}</p>
          ))}
        </div>
      </section>
      <section className="hqmm-panel">
        <header>
          <h2>
            <Users size={17} />
            회의록 기재 명단 <span>{report.data.listedAttendees.length}명</span>
          </h2>
        </header>
        <div className="hqmm-people">
          {report.data.listedAttendees.map((person, index) => (
            <article key={index}>
              <span>
                <Users size={18} />
              </span>
              <div>
                <strong>{person.name}</strong>
                <small>{person.organization}</small>
              </div>
            </article>
          ))}
        </div>
        <p className="hqmm-note">참석 서명지와 원본 파일은 아직 연결되지 않았습니다.</p>
      </section>
      <details className="hqmm-source-notes">
        <summary>원문 근거 보기</summary>
        {report.sourceRefs.map((row, index) => (
          <div key={index}>
            <strong>{row.location}</strong>
            <p>{row.text}</p>
          </div>
        ))}
      </details>
    </article>
  );
}
